// Generated by GitHub Copilot
import React, { useState, useEffect, useRef } from "react";
import { Nav, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaBolt,
  FaMagnifyingGlass,
  FaPen,
  FaShare,
  FaMeteor,
  FaFloppyDisk,
  FaGear,
  FaSnowflake,
  FaAddressCard,
  FaChartArea,
  FaFireExtinguisher,
  FaUser,
  FaEye,
} from "react-icons/fa6";
import { authFetch } from "../../utils/authFetch";
import "./LeftNavigation.css";

function LeftNavigation({
  user,
  newrelic,
  problemId,
  archived,
  isHistoricalVersion,
  viewLink,
  onReportImpact, // Optional callback to open impact modal on current page
  showEditButton = true,
  showImpactCommandCenter = false,
  showTabs = false,
  activeTab,
  onTabChange,
  tabs = [],
  autoSaveStatus = "idle",
  autoSaveError = null,
  // US2138216: lets the host page hold an action until the user confirms
  // (e.g. an unsaved PRBT number). Called as (destination, proceed); proceed
  // optionally takes a rewritten path. Absent, every action runs immediately.
  onBeforeNavigate,
}) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchError, setSearchError] = useState("");

  // Search guards after an async lookup, so it must ask the host's current
  // guard, not the one from the click. Once this instance has unmounted (a
  // promotion remounts the page) there is no page left to hold the exit.
  const onBeforeNavigateRef = useRef(onBeforeNavigate);
  onBeforeNavigateRef.current = onBeforeNavigate;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const guard = (destination, proceed) => {
    const hold = onBeforeNavigateRef.current;
    return hold && mountedRef.current ? hold(destination, proceed) : proceed();
  };

  const createNewProblem = () => {
    let requestOptions = {
      method: "POST",
      body: JSON.stringify({}),
    };

    fetch("/api/v1/problem", requestOptions)
      .then((response) => {
        if (response.status !== 200) {
          // Handle error
        }
        return response.json();
      })
      .then((result) => {
        navigate("/problem/edit/" + result.problem.number);
      });
  };

  // Guarded before the POST, not just before the navigate: choosing to stay
  // must not leave a freshly created record behind.
  const newProblem = () => guard("new-problem", createNewProblem);

  const speedyProblem = () => {
    const destination = "/problem/new/speedy";
    guard(destination, (to = destination) => navigate(to));
  };

  const editProblem = () => {
    if (problemId) {
      const destination = `/problem/edit/${problemId}`;
      guard(destination, (to = destination) => navigate(to));
    }
  };

  // Generated by GitHub Copilot
  const viewProblem = () => {
    if (viewLink) {
      guard(viewLink, (to = viewLink) => navigate(to));
    }
  };

  const copyLink = () => {
    if (viewLink) {
      navigator.clipboard.writeText(
        `${window.location.origin}${viewLink}`
      );
    }
  };

  // Generated by GitHub Copilot - Open impact modal or navigate to impact page
  const reportImpact = () => {
    if (onReportImpact) {
      // If callback is provided, use it (opens modal on current page)
      onReportImpact();
    } else if (problemId) {
      // Otherwise, navigate to impact page with modal auto-open
      const destination = `/problem/impact/${problemId}?openModal=true`;
      guard(destination, (to = destination) => navigate(to));
    }
  };

  const setSearch = (event) => {
    setSearchTerm(event.target.value);
    // Clear a stale validation error as soon as the user edits the term.
    if (searchError) {
      setSearchError("");
    }
  };

  const searchClick = () => {
    // Clear any prior inline error before starting a new search.
    setSearchError("");

    // Guard an empty/whitespace-only term: no-op rather than hitting the
    // resolver with an empty path segment or navigating anywhere.
    // Normalize to upper-case: PRB/INC numbers are upper-case, so "inc4449471"
    // and "INC4449471" resolve identically (the validators are case-sensitive).
    const trimmedTerm = searchTerm.trim().toUpperCase();
    if (!trimmedTerm) {
      return;
    }

    // US2031592: resolve either an INC or a PRB via B4's ticket resolver.
    const ticketURL = "/api/v1/tickets/" + encodeURIComponent(trimmedTerm);

    authFetch(ticketURL)
      .then((response) => {
        if (!response.ok) {
          // 404 → not resolvable. A PRB not yet in Problem Notes is the
          // "search-then-create" case → route to the create flow. An INC, though,
          // must already exist in ServiceNow, so a 404 means it can't be opened —
          // show an inline message instead of creating a (wrong-keyed) record.
          if (response.status === 404) {
            try {
              newrelic?.addPageAction?.('Search.ProblemQuery', {
                query_length: searchTerm.trim().length,
                result_count: 0,
                result_prb: null,
              });
            } catch (nrErr) {
              console.warn('[LeftNavigation] Search.ProblemQuery tracking failed:', nrErr);
            }
            if (trimmedTerm.startsWith('INC')) {
              setSearchError(`${trimmedTerm} was not found`);
            } else {
              // Guarded here rather than before the lookup: an outcome that
              // stays on the page must never prompt, and a lookup that
              // outlives this instance (a promotion remounts the page) still
              // navigates where a state update would be lost.
              const destination = "/problem/start/" + trimmedTerm;
              guard(destination, (to = destination) => navigate(to));
            }
            return;
          }

          // 400 → the term is not a valid PRB or INC: surface an inline error and
          // do NOT route to the create flow.
          if (response.status === 400) {
            setSearchError("Not a valid PRB or INC number");
            return;
          }

          // 409 → the INC is a real command-center incident in ServiceNow, but has
          // no Problem Notes record yet. Search is read-only (it no longer
          // auto-creates a record on first open), so point the user at the explicit
          // create flows instead of navigating to a record that does not exist.
          if (response.status === 409) {
            // US2083214 observability: this replaces a path that used to succeed
            // (search auto-created and opened the record), so it must be
            // measurable — how often MIMs hit "create it first" is the signal for
            // whether the redirect is costing them time during an incident. Named
            // distinctly from Search.ProblemQuery so it can be alerted on
            // separately without polluting the search result-count metric.
            try {
              newrelic?.addPageAction?.('Search.INCNotInProblemNotes', {
                query_length: searchTerm.trim().length,
              });
            } catch (nrErr) {
              console.warn('[LeftNavigation] Search.INCNotInProblemNotes tracking failed:', nrErr);
            }
            setSearchError(`${trimmedTerm} must be created first; use New Problem or Speedy Form`);
            return;
          }

          // 422 → resolvable but not trackable (an INC not in an accepted
          // command-center assignment group). Surface the reason inline; do NOT navigate.
          if (response.status === 422) {
            setSearchError(`${trimmedTerm} is not assigned to an appropriate assignment group`);
            return;
          }

          throw Error("oops");
        }

        // Return the inner promise so a malformed-but-2xx body (bad JSON, or a
        // missing problem/number) rejects into the outer .catch → generic error,
        // rather than throwing an unhandled rejection that bypasses it.
        return response.json().then((res) => {
          const prb = res?.problem;
          if (!prb || !prb.number) {
            throw new Error('malformed ticket response');
          }

          try {
            newrelic?.addPageAction?.('Search.ProblemQuery', {
              query_length: searchTerm.trim().length,
              result_count: 1,
              result_prb: prb.number,
            });
          } catch (nrErr) {
            console.warn('[LeftNavigation] Search.ProblemQuery tracking failed:', nrErr);
          }
          // Preserve the pre-US2031592 landing URL: deep-link to the first outage
          // (`/problem/view/{number}/outage/{firstOutage}`) so the view page and its
          // Share Link stay byte-for-byte unchanged. `prb.outages` is the record's
          // OutageMap (json:"outages"); fall back to the outage-less route when a
          // record has no outages (e.g. a not-yet-populated skeleton).
          const firstOutageID = Object.keys(prb.outages || {})[0];
          const destination = firstOutageID
            ? `/problem/view/${prb.number}/outage/${firstOutageID}`
            : `/problem/view/${prb.number}`;
          guard(destination, (to = destination) => navigate(to));
        });
      })
      .catch((error) => {
        console.warn('[LeftNavigation] search failed:', error);
        setSearchError('Something went wrong — please try again');
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    searchClick();
  };

  return (
    <div className="left-navigation">
      {/* Search Section */}
      {user?.privileges?.search && (
        <div className="left-nav-section search-section">
          <Form className="searchForm" onSubmit={handleSubmit}>
            <InputGroup>
              <Form.Control
                type="search"
                placeholder="PRB / INC #"
                aria-label="Search"
                aria-invalid={!!searchError}
                aria-describedby={searchError ? "search-error" : undefined}
                value={searchTerm}
                onChange={setSearch}
                data-testid="search-input"
                size="sm"
              />
              <Button
                variant="secondary"
                onClick={searchClick}
                data-testid="search-button"
                size="sm"
              >
                <FaMagnifyingGlass />
              </Button>
            </InputGroup>
            {searchError && (
              <div
                id="search-error"
                role="alert"
                className="search-error"
                data-testid="search-error"
              >
                {searchError}
              </div>
            )}
          </Form>
        </div>
      )}

      {/* Action Buttons Section */}
      {/* Generated by GitHub Copilot - US1812367: Updated to full-width buttons with text */}
      <div className="left-nav-section actions-section">
        <div className="action-buttons">
          {user?.privileges?.create && (
            <>
              <Button
                variant="primary"
                onClick={newProblem}
                data-testid="new-button"
                className="action-btn"
              >
                <FaPlus className="me-2" /> New Problem
              </Button>
              <Button
                variant="warning"
                onClick={speedyProblem}
                data-testid="speedy-button"
                className="action-btn"
              >
                <FaBolt className="me-2" /> Speedy Form
              </Button>
            </>
          )}
          {user?.privileges?.editor &&
            problemId &&
            !archived &&
            !isHistoricalVersion &&
            showEditButton && (
              <Button
                variant="danger"
                onClick={editProblem}
                data-testid="edit-button"
                className="action-btn"
              >
                <FaPen className="me-2" /> Edit Problem
              </Button>
            )}
          {/* Generated by GitHub Copilot */}
          {viewLink && !showEditButton && problemId && (
            <Button
              variant="primary"
              onClick={viewProblem}
              data-testid="view-button"
              className="action-btn"
            >
              <FaEye className="me-2" /> View Problem
            </Button>
          )}
          {viewLink && (
            <Button
              variant="secondary"
              onClick={copyLink}
              data-testid="copy-button"
              className="action-btn"
            >
              <FaShare className="me-2" /> Share Link
            </Button>
          )}
          {/* Generated by GitHub Copilot - Auto-Save Status Button - Only show in edit mode */}
          {autoSaveStatus && autoSaveStatus !== "idle" && (
            <Button
              variant={
                autoSaveStatus === "saving"
                  ? "warning"
                  : autoSaveStatus === "saved"
                  ? "success"
                  : autoSaveStatus === "error"
                  ? "danger"
                  : autoSaveStatus === "conflict"
                  ? "warning"
                  : "secondary"
              }
              disabled={true}
              data-testid="save-button"
              title={
                autoSaveStatus === "saving"
                  ? "Saving changes..."
                  : autoSaveStatus === "saved"
                  ? "All changes saved"
                  : autoSaveStatus === "error"
                  ? `Save failed: ${autoSaveError?.message || "Unknown error"}`
                  : autoSaveStatus === "conflict"
                  ? "Reloaded to the latest version — re-apply your change if it's still needed"
                  : "Auto-save enabled"
              }
              size="sm"
              className="action-btn"
            >
              {autoSaveStatus === "saving" ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : autoSaveStatus === "saved" ? (
                <>
                  <FaFloppyDisk className="me-2" />
                  Saved
                </>
              ) : autoSaveStatus === "error" ? (
                <>
                  <FaFloppyDisk className="me-2" />
                  Error
                </>
              ) : autoSaveStatus === "conflict" ? (
                <>
                  <FaFloppyDisk className="me-2" />
                  Reloaded
                </>
              ) : (
                <>
                  <FaFloppyDisk className="me-2" />
                  Auto-Save
                </>
              )}
            </Button>
          )}
          {/* Generated by GitHub Copilot - US1812367: Report Impact button */}
          {showImpactCommandCenter &&
            problemId &&
            !archived &&
            !isHistoricalVersion && (
              <Button
                variant="success"
                onClick={reportImpact}
                data-testid="report-impact-button"
                className="action-btn"
              >
                <FaMeteor className="me-2" /> Report Impact
              </Button>
            )}
        </div>
      </div>

      {/* Tab Navigation Section - Only show if tabs are provided */}
      {showTabs && tabs.length > 0 && (
        <div className="left-nav-section tabs-section">
          <Nav className="flex-column">
            {tabs.map((tab) => (
              <Nav.Link
                key={tab.key}
                active={activeTab === tab.key}
                onClick={() => onTabChange && onTabChange(tab.key)}
                data-testid={`sidebar-tab-${tab.key}`}
              >
                {tab.label}
              </Nav.Link>
            ))}
          </Nav>
        </div>
      )}

      {/* Links Section - Always show at bottom */}
      <div className="left-nav-section links-section">
        <Nav className="flex-column">
          {user && user.privileges && user.privileges.configuration && (
            <Nav.Link href={"/configuration"} title="Configuration">
              <FaGear />
            </Nav.Link>
          )}
          {user && user.privileges && user.privileges.deity && (
            <Nav.Link href={"/manage/users"} title="User Management">
              <FaSnowflake />
            </Nav.Link>
          )}
          <Nav.Link href={"/about"} title="About">
            <FaAddressCard />
          </Nav.Link>
          <Nav.Link href={"/system"} title="System Status">
            <FaChartArea />
          </Nav.Link>
          <Nav.Link href={"/profile"} title="Profile">
            <FaUser />
          </Nav.Link>

          {/* Badge indicators */}
          {user && (user.privileges?.editor || user.privileges?.deity) && (
            <div className="badge-indicators">
              {user.privileges?.editor && (
                <span title="Authorized Editor">
                  <FaFireExtinguisher />
                </span>
              )}
              {user.privileges?.deity && (
                <span title="Deity">
                  <FaSnowflake />
                </span>
              )}
            </div>
          )}
        </Nav>
      </div>
    </div>
  );
}

export default LeftNavigation;
